import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MyButton } from '../index';
import FormField from '../FormField/FormField';
import { validateField, validateForm } from './FormValidation';
import { dynamicFormStyles } from './DynamicForm.styles';
import CommentSection from '../CommentSection/CommentSection';
import { parseDateForForm } from '../../utils/dateUtils';
import { useAuth } from '../../hooks';

const DATE_FIELD_TYPES = new Set(['date', 'datetime-local']);

const restoreDateFields = (data, fieldsList) => {
  if (!data || typeof data !== 'object') return data;
  const restored = { ...data };

  fieldsList.forEach(field => {
    if (DATE_FIELD_TYPES.has(field.type) && field.name in restored) {
      restored[field.name] = parseDateForForm(restored[field.name]);
    }
    if (field.type === 'array' && Array.isArray(restored[field.name])) {
      restored[field.name] = restored[field.name].map(item => {
        if (!item || typeof item !== 'object') return item;
        const nextItem = { ...item };
        (field.itemFields || []).forEach(itemField => {
          if (DATE_FIELD_TYPES.has(itemField.type) && itemField.name in nextItem) {
            nextItem[itemField.name] = parseDateForForm(nextItem[itemField.name]);
          }
        });
        return nextItem;
      });
    }
  });

  return restored;
};

const DynamicForm = ({
  fields = [],
  initialData = {},
  validationRules = {},
  onSubmit,
  onCancel,
  loading = false,
  submitText,
  cancelText,
  showCancelButton = true,
  title,
  entityId = null,
  commentEntityType = null,
  // Bir field'in altina custom React node basmak icin (ornegin secilen
  // is icin "daha once N ay girilmis" gibi info kartlari). Sayfa
  // (field.name, formData, currentUser) alir, React node veya null doner.
  renderFieldExtras = null,
  ...props
}) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);


  // Form reset SADECE initialData'nin ICERIGI (deger olarak) degistiginde
  // tetiklenir. Referans karsilastirmasi ({} her render'da yeni obje) reset'i
  // sik sik tetikleyip kullanicinin doldurdugu formu siliyordu. JSON.stringify
  // ile deep-compare yaparak sadece gercekten yeni bir kayit acildiginda
  // reset yapiyoruz. Bu, submit'te blocker/hata donerse formu kalici tutar.
  //
  // fields dep'te YOK — sayfa fields'i runtime options ile yeniden ureterek
  // form'u silmemeli.
  const initialDataKey = JSON.stringify(initialData);
  useEffect(() => {
    const parsed = JSON.parse(initialDataKey);
    const defaultValues = {};
    fields.forEach(field => {
      if (
        field.defaultValue &&
        (parsed[field.name] === null ||
          parsed[field.name] === undefined ||
          !(field.name in parsed))
      ) {
        defaultValues[field.name] = field.defaultValue;
      }
    });

    const finalInitialData = restoreDateFields(
      { ...defaultValues, ...parsed },
      fields,
    );
    setFormData(finalInitialData);
    setErrors({});
    setTouched({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDataKey]);

  const allFields = fields;

  const extractedValidationRules = React.useMemo(() => {
    if (Object.keys(validationRules).length > 0) {
      return validationRules;
    }

    const extracted = {};
    fields.forEach(field => {
      if (field.validation) {
        extracted[field.name] = field.validation;
      }
    });
    return extracted;
  }, [fields, validationRules]);

  const isEditMode = !!entityId;

  const handleValidateField = (fieldName, value, allFormData = formData) => {
    return validateField(
      fieldName,
      value,
      extractedValidationRules,
      allFormData,
      t,
      isEditMode
    );
  };

  const handleValidateForm = () => {
    const newErrors = validateForm(
      allFields,
      formData,
      extractedValidationRules,
      t,
      isEditMode
    );
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (fieldName, value, event) => {
    // Functional setState — ard arda gelen onChange cagrilari (orn.
    // NumberWithButtonField'in tutar + manualFlagField ikilisi) React
    // batching yuzunden birbirini override etmesin. Her cagri en son
    // state'i baz alir.
    let latest = null;
    setFormData((prev) => {
      let next = { ...prev, [fieldName]: value };
      const changedField = fields.find(f => f.name === fieldName);
      if (changedField && typeof changedField.onChangeSideEffects === 'function') {
        try {
          const patch = changedField.onChangeSideEffects(value, next);
          if (patch && typeof patch === 'object') {
            next = { ...next, ...patch };
          }
        } catch (e) {
          console.warn('[DynamicForm] onChangeSideEffects error', e);
        }
      }
      latest = next;
      return next;
    });
    const newFormData = latest || { ...formData, [fieldName]: value };

    if (touched[fieldName]) {
      const error = handleValidateField(fieldName, value, newFormData);
      setErrors(prev => ({
        ...prev,
        [fieldName]: error,
      }));
    }
  };

  const handleFieldBlur = (fieldName, value, event) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));

    const error = handleValidateField(fieldName, value);
    setErrors(prev => ({
      ...prev,
      [fieldName]: error,
    }));
  };

  const handleSubmit = async event => {
    event.preventDefault();

    if (!handleValidateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (onSubmit) {
        await onSubmit(formData);
      }
    } catch (error) {
      // Error handling done by caller
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const defaultSubmitText = submitText || t('common.save');
  const defaultCancelText = cancelText || t('common.cancel');

  const renderFields = fieldsToRender => {
    return fieldsToRender
      .map(field => {
        if (field.showIf && !field.showIf(formData, currentUser)) {
          return null;
        }

        // Handle dynamic required / disabled / hideButton fields
        const processedField = {
          ...field,
          required: typeof field.required === 'function'
            ? field.required(formData, null, isEditMode)
            : field.required,
          disabled: typeof field.disabled === 'function'
            ? field.disabled(formData, currentUser, isEditMode)
            : field.disabled,
          hideButton: typeof field.hideButton === 'function'
            ? field.hideButton(formData, currentUser)
            : field.hideButton,
        };

        const extras = renderFieldExtras
          ? renderFieldExtras(field.name, formData, currentUser, handleFieldChange)
          : null;

        if (!extras) {
          return (
            <FormField
              key={field.name}
              field={processedField}
              value={formData[field.name]}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={errors[field.name]}
              disabled={loading || isSubmitting || !!processedField.disabled}
              formData={formData}
              entityId={entityId}
            />
          );
        }

        // Extras varsa: field ile ekstranin ust uste inmesi icin ayni satirda
        // sarilir (fullWidth kirilimini korur).
        return (
          <div key={field.name} style={{ width: '100%' }}>
            <FormField
              field={processedField}
              value={formData[field.name]}
              onChange={handleFieldChange}
              onBlur={handleFieldBlur}
              error={errors[field.name]}
              disabled={loading || isSubmitting || !!processedField.disabled}
              formData={formData}
              entityId={entityId}
            />
            {extras}
          </div>
        );
      })
      .filter(Boolean);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={dynamicFormStyles.form}>
      {title && (
        <Typography variant="h6" sx={dynamicFormStyles.title}>
          {title}
        </Typography>
      )}
      <Box sx={dynamicFormStyles.fieldsContainer}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
          {renderFields(allFields)}
        </div>
      </Box>
      {commentEntityType && entityId && (
        <>
          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              {t('comments.title', 'Yorumlar')}
            </Typography>
          </Divider>
          <CommentSection entityType={commentEntityType} entityId={entityId} />
          <Divider sx={{ mt: 1 }} />
        </>
      )}
      <Box sx={dynamicFormStyles.actions}>
        {showCancelButton && (
          <MyButton
            variant="outlined"
            onClick={handleCancel}
            disabled={isSubmitting}
            sx={dynamicFormStyles.cancelButton}
          >
            {defaultCancelText}
          </MyButton>
        )}
        <MyButton
          type="submit"
          variant="contained"
          disabled={loading || isSubmitting}
          sx={dynamicFormStyles.submitButton}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              {t('modals.form.saving')}
            </>
          ) : (
            defaultSubmitText
          )}
        </MyButton>
      </Box>
    </Box>
  );
};

export default DynamicForm;
